import { useCallback, useState } from "react";
import { getBuilderWarnings, validateFormAnswers, type BuilderDraft, type FormAnswer, type FormAnswers } from "@froppio/form-core";
import type { FormRendererComponents } from "./types";
import { FormPreviewField } from "./FormPreviewField";

export interface FormPreviewProps {
  draft: BuilderDraft;
  components: FormRendererComponents;
}

export function FormPreview({ draft, components }: FormPreviewProps) {
  const { Section } = components;
  const [answers, setAnswers] = useState<FormAnswers>({});
  const [controlErrors, setControlErrors] = useState<Record<string, string>>({});
  const warnings = getBuilderWarnings(draft);
  const errors = validateFormAnswers(draft, answers);

  const changeAnswer = useCallback((id: string, value: FormAnswer) => {
    setAnswers(previous => ({ ...previous, [id]: value }));
  }, []);
  const changeControlError = useCallback((id: string, error: string | undefined) => {
    setControlErrors(previous => {
      if (previous[id] === error) return previous;
      const next = { ...previous };
      if (error) next[id] = error;
      else delete next[id];
      return next;
    });
  }, []);

  function renderField(id: string) {
    const field = draft.fields[id];
    if (!field) return <p key={id} role="alert" className="text-sm text-destructive">This question is missing from the definition.</p>;
    return <FormPreviewField key={id} components={components} field={field} value={answers[id]} error={controlErrors[id] || (id in answers ? errors[id] : undefined)} onChange={changeAnswer} onControlError={changeControlError} />;
  }
  return (
    <div className="space-y-6" onKeyDown={event => {
      if (event.key === "Enter" && event.target instanceof HTMLInputElement) event.preventDefault();
    }}>
      <p className="text-sm text-muted-foreground">Interactive preview only. Answers and signatures are not submitted or saved, and are discarded when you leave preview.</p>
      {warnings.length > 0 && (
        <Section title="Complete your form before using it" card>
          <ul className="list-disc space-y-1 pl-5 text-sm text-muted-foreground">
            {warnings.map((warning, index) => <li key={`${warning.id}-${index}`}>{warning.message}</li>)}
          </ul>
        </Section>
      )}
      <article aria-label="Form preview" className="min-w-0 space-y-8 rounded-[20px] border border-border bg-card p-5 shadow-sm sm:p-10">
        {draft.elements.map(element => element.type === "field" ? renderField(element.fieldId) : (
          <Section key={element.id} title={element.title.trim() ? element.title : "Untitled section"}>
            {element.description && <p className="whitespace-pre-wrap text-sm text-muted-foreground">{element.description}</p>}
            <div className="space-y-[18px]">{element.elements.map(child => renderField(child.fieldId))}</div>
            {!element.elements.length && <p className="text-sm text-muted-foreground">This section has no questions yet.</p>}
          </Section>
        ))}
        {!draft.elements.length && <p className="text-sm text-muted-foreground">No questions yet.</p>}
      </article>
    </div>
  );
}
